import React, { useEffect, useState } from "react";
import Select, { MultiValue } from "react-select";
import makeAnimated from "react-select/animated";
import { v4 as uuidv4 } from "uuid";
import { CommonModal } from "@src/components/modal/CommonModal";
import { Loader } from "@src/components/loader/Loader";
import EventContextHeader, { TContextEvent, TContextListing } from "@src/components/eventContextHeader/EventContextHeader";
import styles from "./InternalNotesModal.module.scss";
import { Button, ButtonVariant } from "@src/components/button/Button";
import { Switch } from "@src/components/switch/Switch";
import UniversalInput from "@src/components/universalInput/UniversalInput";
import { XIcon } from "@src/assets/general-icons";
import { toast } from "sonner";
import PlusIconThemed from "@src/assets/general-icons/PlusIconThemed";
import { Trashcan } from "@src/assets/general-icons/Trashcan";
import { useAppSelector } from "@src/store/hooks";
import { Theme } from "@src/store/settingsSlice";
import { ApiProvider } from "@src/store/ApiProvider";

export interface InternalNotesAccountData {
	email: string;
	orderId: string;
	password: string;
	seats: Array<string>;
}

export interface InternalNotesLocalData {
	accounts: Array<InternalNotesAccountData>;
	eventSite: string;
	isMasked: boolean;
	maskedSection: string;
	maskedRow: string;
	maskedSeat: string;
	comments: string;
	transferlessLink: string;
}

interface IProps {
	onClose: () => void;
	listingId?: string | null | undefined;
	invoiceId?: string | null | undefined;
	eventHeaderListings?: Array<TContextListing>;
	eventHeaderEvents?: Array<TContextEvent>;
	localMode?: boolean;
	localSeatOptions?: Array<SelectOption<string>>;
	initialLocalData?: InternalNotesLocalData | null;
	onLocalSave?: (data: InternalNotesLocalData) => void;
	onSaved?: () => void;
}

interface SelectOption<T> {
	label: string;
	value: T;
}

interface Account {
	id: string;
	email: string;
	orderId: string;
	password: string;
	seats: Array<SelectOption<string>>;
	itemStatusIds: Array<number>;
}

interface VendorParent {
	vendorParentId: string;
	name: string;
	vendorId?: string;
}

interface Vendor {
	vendorId: string;
	name: string;
	url: string;
	vendorParentID: string;
}

type EventSiteOption = SelectOption<VendorParent>;
type VendorOption = SelectOption<Vendor>;
type SeatOption = SelectOption<string>;

const getCompactSelectStyles = (theme: Theme, dense = false) => {
	const isLight = theme === Theme.Light;
	const bg = isLight ? "#fafafa" : "#0d0d0d";
	const bgSelected = isLight ? "#EDEEF4" : "#2f2f2f";
	const border = isLight ? "#ddddda" : "#2f2f2f";
	const textColor = isLight ? "#212121" : "#fafafa";
	const placeholderColor = isLight ? "#888888" : "#a3a3a3";
	const hoverBorder = isLight ? "#888888" : "#a3a3a3";

	return {
		control: (base: Record<string, unknown>) => ({
			...base,
			minHeight: dense ? "28px" : "34px",
			fontSize: "1.3rem",
			marginTop: "0px",
			padding: "0 4px",
			borderRadius: "6px",
			background: bg,
			border: `1px solid ${border}`,
			boxShadow: "none",
			"&:hover": { borderColor: hoverBorder },
		}),
		menu: (base: Record<string, unknown>) => ({
			...base,
			fontSize: "1.3rem",
			zIndex: 10,
			background: bg,
			border: `1px solid ${border}`,
		}),
		menuList: (base: Record<string, unknown>) => ({
			...base,
			maxHeight: dense ? "120px" : "180px",
		}),
		valueContainer: (base: Record<string, unknown>) => ({
			...base,
			padding: "2px 4px",
			fontSize: "1.3rem",
		}),
		singleValue: (base: Record<string, unknown>) => ({
			...base,
			color: textColor,
		}),
		input: (base: Record<string, unknown>) => ({
			...base,
			margin: "0",
			padding: "0",
			fontSize: "1.3rem",
			color: textColor,
		}),
		indicatorsContainer: (base: Record<string, unknown>) => ({
			...base,
			fontSize: "1.3rem",
		}),
		option: (base: Record<string, unknown>, state: { isSelected: boolean }) => ({
			...base,
			fontSize: dense ? "1.2rem" : "1.3rem",
			padding: dense ? "3px 10px" : "8px 12px",
			backgroundColor: state.isSelected ? bgSelected : bg,
			color: textColor,
			"&:hover": { backgroundColor: bgSelected },
		}),
		placeholder: (base: Record<string, unknown>) => ({
			...base,
			color: placeholderColor,
		}),
		multiValue: (base: Record<string, unknown>) => ({
			...base,
			backgroundColor: bgSelected,
			border: `1px solid ${border}`,
			borderRadius: "4px",
		}),
		multiValueLabel: (base: Record<string, unknown>) => ({
			...base,
			color: textColor,
			fontSize: "1.2rem",
		}),
		multiValueRemove: (base: Record<string, unknown>) => ({
			...base,
			color: textColor,
			"&:hover": {
				backgroundColor: hoverBorder,
				color: isLight ? "#fff" : "#000",
			},
		}),
	};
};

const FLAG_TOKENS = [
	{ key: "noPasswordManager" as const, abbr: "-NoPasswordManager", label: "No Password Manager" },
	{ key: "anonymousTransfer" as const, abbr: "-AnonymousTransfer", label: "Anonymous Transfer" },
];

const abbreviatedEventSites = [
	{ name: "All", abbr: "-all" },
	{ name: "FanXchange", abbr: "-fanx" },
	{ name: "Gametime", abbr: "-gt" },
	{ name: "GoTickets", abbr: "-gotix" },
	{ name: "SeatGeek", abbr: "-sg" },
	{ name: "Ticket Evolution", abbr: "-tevo" },
	{ name: "TicketNetwork", abbr: "-tnd" },
	{ name: "TicketsNow", abbr: "-tnow" },
	{ name: "TM+", abbr: "-tm+" },
	{ name: "TickPick", abbr: "-tpick" },
	{ name: "Viagogo / Stubhub", abbr: "-Vgg" },
	{ name: "Vivid Seats", abbr: "-vivid" },
];

let cachedVendorParents: Array<VendorParent> | null = null;
let cachedVendors: Array<Vendor> | null = null;
let inFlightVendorParents: Promise<Array<VendorParent>> | null = null;
let inFlightVendors: Promise<Array<Vendor>> | null = null;

const InternalNotesModal = ({
	onClose,
	onSaved,
	listingId,
	invoiceId,
	eventHeaderListings,
	eventHeaderEvents,
	localMode,
	localSeatOptions,
	initialLocalData,
	onLocalSave,
}: IProps) => {
	const [isMasked, setIsMasked] = useState<boolean>(false);
	const [maskedSection, setMaskedSection] = useState<string>("");
	const [maskedRow, setMaskedRow] = useState<string>("");
	const [maskedSeat, setMaskedSeat] = useState<string>("");
	const [chosenEventSite, setChosenEventSite] = useState<EventSiteOption | null>(null);
	const [chosenEventSiteDetail, setChosenEventSiteDetail] = useState<VendorOption | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [accounts, setAccounts] = useState<Array<Account>>([]);
	const [seatOptions, setSeatOptions] = useState<Array<SeatOption>>([]);
	const [comments, setComments] = useState<string>("");
	const [transferlessLink, setTransferlessLink] = useState<string>("");
	const [eventSites, setEventSites] = useState<Array<EventSiteOption>>([]);
	const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
	const [filteredEventSites, setFilteredEventSites] = useState<Array<VendorOption>>([]);
	const [eventSiteDetails, setEventSiteDetails] = useState<Array<Vendor>>([]);
	const [linkUrl, setLinkUrl] = useState<string>("");
	const [seatToItemStatusMap, setSeatToItemStatusMap] = useState<Record<string, number>>({});
	const [passwordVisible, setPasswordVisible] = useState<Record<string, boolean>>({});
	const [selectedExclusions, setSelectedExclusions] = useState<Array<SelectOption<string>>>([]);
	const [noPasswordManager, setNoPasswordManager] = useState<boolean>(false);
	const [anonymousTransfer, setAnonymousTransfer] = useState<boolean>(false);

	const addAccount = () => {
		const newAccount = {
			id: uuidv4(),
			email: "",
			orderId: "",
			password: "",
			seats: [],
			itemStatusIds: [],
		};
		setAccounts([...accounts, newAccount]);
	};

	const removeAccount = (accountId: string) => {
		setAccounts(accounts.filter((account) => account.id !== accountId));
	};

	const togglePasswordVisibility = (accountId: string) => {
		setPasswordVisible((prev) => ({
			...prev,
			[accountId]: !prev[accountId],
		}));
	};

	const updateAccount = (accountId: string, changes: Partial<Account>) => {
		setAccounts((prev) => prev.map((acc) => (acc.id === accountId ? { ...acc, ...changes } : acc)));
	};

	const fetchEventSites = async (): Promise<void> => {
		try {
			if (!cachedVendorParents) {
				if (!inFlightVendorParents) {
					inFlightVendorParents = ApiProvider.default
						.get<Array<VendorParent>>("/api/Vendor/vendorParents")
						.then((data) => {
							cachedVendorParents = data;
							return data;
						})
						.finally(() => {
							inFlightVendorParents = null;
						});
				}
				await inFlightVendorParents;
			}
			const data = cachedVendorParents ?? [];
			setEventSites(data.map((item) => ({ value: item, label: item.name })));
		} catch (error) {
			console.error("Error fetching event sites:", error);
		}
	};

	const fetchVendors = async (): Promise<void> => {
		try {
			if (!cachedVendors) {
				if (!inFlightVendors) {
					inFlightVendors = ApiProvider.default
						.get<Array<Vendor>>("/api/Vendor/vendors")
						.then((data) => {
							cachedVendors = data;
							return data;
						})
						.finally(() => {
							inFlightVendors = null;
						});
				}
				await inFlightVendors;
			}
			setEventSiteDetails(cachedVendors ?? []);
		} catch (error) {
			console.error("Error fetching vendors:", error);
		}
	};

	const handleManagerChange = (selectedOption: VendorOption | null): void => {
		setLinkUrl(selectedOption?.value?.url || "");
		setSelectedVendorId(selectedOption?.value.vendorId || null);
		setChosenEventSiteDetail(selectedOption);
	};

	const getEventSiteOptions = (vendorParentId: string): void => {
		const eventSitesFilteredByVendorId = eventSiteDetails.filter((item) => item.vendorParentID === vendorParentId);

		const mappedEventSites: Array<VendorOption> = eventSitesFilteredByVendorId.map((v) => ({
			label: v.name,
			value: v,
		}));

		setFilteredEventSites(mappedEventSites);
	};

	const handleEventSiteChange = (selectedEventSite: EventSiteOption | null): void => {
		setChosenEventSite(selectedEventSite);
		setSelectedVendorId(null);
		setLinkUrl("");
		setChosenEventSiteDetail(null);

		if (selectedEventSite) {
			const vendorParent = selectedEventSite.value;
			if (vendorParent && !vendorParent.vendorId) {
				getEventSiteOptions(vendorParent.vendorParentId);
			}
		}
	};

	const allAccountsHaveSeats = () => {
		return accounts.every((account) => account.seats.length > 0);
	};

	const shouldDisableSave = () => {
		if (localMode) {
			return comments.trim().length === 0;
		}
		if (accounts.length === 0) {
			return true;
		}
		let totalSelectedSeats = 0;
		accounts.forEach((x) => (totalSelectedSeats += x.seats.length));
		return totalSelectedSeats !== seatOptions.length;
	};

	const save = (): void => {
		if (localMode && onLocalSave) {
			onLocalSave({
				accounts: accounts.map((a) => ({
					email: a.email,
					orderId: a.orderId,
					password: a.password,
					seats: a.seats.map((s) => String(s.value)),
				})),
				eventSite: chosenEventSite?.label ?? "",
				isMasked,
				maskedSection,
				maskedRow,
				maskedSeat,
				comments,
				transferlessLink,
			});
			onClose();
			return;
		}

		const isEmailsValid = accounts.every((account) => {
			if (account.email.length === 0) return true;
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			return emailRegex.test(account.email);
		});

		if (!allAccountsHaveSeats()) {
			toast("Please select a seat for each account.");
			return;
		}

		if (!isEmailsValid) {
			toast("Please enter a valid email address.");
			return;
		}

		const requestData = accounts.flatMap((account) => {
			// TAC-2365: exclude the password from the `...account` spread and only emit it when the user
			// actually typed a new value. Omitting the field leaves the stored credential untouched
			// (internal-api skips the credential update when the password is empty/absent), so routine
			// edits to other fields never wipe stored vendor credentials.
			const { password: typedPassword, ...accountFields } = account;
			return account.seats.map((seat) => {
				const itemSeat = {
					itemId: seat.value,
					user: "Admin",
					internalNote: {
						...accountFields,
						orderId: account.orderId,
						email: account.email,
						...(typedPassword ? { password: typedPassword } : {}),
						eventSite: chosenEventSite?.label || "",
						transferlessLink: transferlessLink,
						comments: comments,
						vendorId: selectedVendorId || chosenEventSite?.value.vendorId,
						vendorParentId: chosenEventSite?.value.vendorParentId || null,
						...(isMasked && {
							maskedDetails: {
								section: maskedSection,
								row: maskedRow,
								seat: maskedSeat,
							},
						}),
					},
				};
				return itemSeat;
			});
		});

		ApiProvider.default
			.post("/api/InternalNote/BulkUpsert", requestData)
			.then(() => {
				toast("Internal notes saved successfully.");
				onSaved?.();
				onClose();
			})
			.catch((error) => {
				console.error("Error saving data:", error);
				toast("Error saving internal notes.");
			});
	};

	const isAbbrToken = (token: string): boolean =>
		abbreviatedEventSites.some((s) => s.abbr === token) || FLAG_TOKENS.some((f) => f.abbr === token);

	const stripAbbrLines = (text: string): string => {
		return text
			.split("\n")
			.filter((line) => {
				const tokens = line.trim().split(/\s+/).filter(Boolean);
				if (tokens.length === 0) return true;
				return !tokens.every((t) => isAbbrToken(t));
			})
			.join("\n");
	};

	const rebuildComments = (
		base: string,
		exclusions: Array<SelectOption<string>>,
		flagState: { noPasswordManager: boolean; anonymousTransfer: boolean },
	): string => {
		const baseContent = stripAbbrLines(base).replace(/\n+$/g, "");
		const exclusionLine = exclusions.map((o) => o.value).join(" ");
		const flagLine = FLAG_TOKENS.filter((f) => flagState[f.key])
			.map((f) => f.abbr)
			.join(" ");
		return [baseContent, exclusionLine, flagLine].filter(Boolean).join("\n");
	};

	const handleExclusionChange = (selectedOptions: MultiValue<SelectOption<string>> | null): void => {
		const newSelected = selectedOptions ? Array.from(selectedOptions) : [];
		setSelectedExclusions(newSelected);
		setComments((prev) => rebuildComments(prev, newSelected, { noPasswordManager, anonymousTransfer }));
	};

	const handleFlagToggle = (key: "noPasswordManager" | "anonymousTransfer", value: boolean): void => {
		const nextFlagState = {
			noPasswordManager: key === "noPasswordManager" ? value : noPasswordManager,
			anonymousTransfer: key === "anonymousTransfer" ? value : anonymousTransfer,
		};
		if (key === "noPasswordManager") setNoPasswordManager(value);
		if (key === "anonymousTransfer") setAnonymousTransfer(value);
		setComments((prev) => rebuildComments(prev, selectedExclusions, nextFlagState));
	};

	useEffect(() => {
		fetchEventSites();
		fetchVendors();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const headerListing = eventHeaderListings?.[0];
	const defaultMaskedSection = headerListing?.section != null ? String(headerListing.section) : "";
	const defaultMaskedRow = headerListing?.row != null ? String(headerListing.row) : "";
	const defaultMaskedSeat = headerListing?.seats != null ? String(headerListing.seats) : "";

	useEffect(() => {
		if (localMode) {
			if (eventSites.length === 0 || eventSiteDetails.length === 0) return;
			setSeatOptions(localSeatOptions ?? []);
			setMaskedSection(initialLocalData?.maskedSection || defaultMaskedSection);
			setMaskedRow(initialLocalData?.maskedRow || defaultMaskedRow);
			setMaskedSeat(initialLocalData?.maskedSeat || defaultMaskedSeat);
			setIsMasked(initialLocalData ? initialLocalData.isMasked : false);
			if (initialLocalData) {
				setComments(initialLocalData.comments);
				setTransferlessLink(initialLocalData.transferlessLink);
				setChosenEventSite(eventSites.find((s) => s.label === initialLocalData.eventSite) ?? null);
				setAccounts(
					initialLocalData.accounts.map((a) => ({
						id: uuidv4(),
						email: a.email,
						orderId: a.orderId,
						password: a.password,
						seats: a.seats.map((s) => ({ label: s, value: s })),
						itemStatusIds: [],
					})),
				);
			}
			setIsLoading(false);
			return;
		}
		if (!listingId && !invoiceId) {
			setIsLoading(false);
			return;
		}
		if (eventSites.length === 0 || eventSiteDetails.length === 0) return;

		interface NoteItem {
			seat: string;
			itemId: string;
			itemStatusId: number;
			internalNote: {
				orderId: string;
				email: string;
				// TAC-2365: Password is intentionally not read from the API response. The backend
				// excludes it from serialization ([JsonIgnore]) so stored credentials never leave the
				// service. This field is write-only in the UI — see the grouping/save logic below.
				eventSite: string;
				transferlessLink: string;
				comments: string;
				vendorId: string | null;
				vendorParentId: string | null;
				maskedDetails?: {
					section: string;
					row: string;
					seat: string;
				};
			};
		}

		const url =
			invoiceId && invoiceId !== "null"
				? `/api/internalnote/invoiceId/${invoiceId}`
				: `/api/internalnote/listingId/${listingId}`;

		ApiProvider.default
			.get<Array<NoteItem>>(url)
			.then((data) => {
				// Map seat options for the select
				const mappedOptions = data.map((item) => ({
					label: item.seat,
					value: item.itemId,
				}));
				setSeatOptions(mappedOptions);

				const newSeatToItemStatusMap = data.reduce<Record<string, number>>((acc, item) => {
					acc[item.seat] = item.itemStatusId;
					return acc;
				}, {});
				setSeatToItemStatusMap(newSeatToItemStatusMap);

				// Set comments and transferless link
				const uniqueComments = Array.from(new Set(data.map((item) => item.internalNote.comments)));
				const initialComments = uniqueComments.length === 1 ? uniqueComments[0] : uniqueComments.join("\n");
				setComments(initialComments);

				const commentTokens = initialComments.split(/\s+/).filter(Boolean);
				const initialExclusions = abbreviatedEventSites
					.filter((s) => commentTokens.includes(s.abbr))
					.map((s) => ({ value: s.abbr, label: s.name }));
				setSelectedExclusions(initialExclusions);
				const nopmToken = FLAG_TOKENS.find((f) => f.key === "noPasswordManager")?.abbr;
				const anonToken = FLAG_TOKENS.find((f) => f.key === "anonymousTransfer")?.abbr;
				if (nopmToken && commentTokens.includes(nopmToken)) setNoPasswordManager(true);
				if (anonToken && commentTokens.includes(anonToken)) setAnonymousTransfer(true);

				const uniqueTransferlessLinks = Array.from(new Set(data.map((item) => item.internalNote.transferlessLink)));
				setTransferlessLink(
					uniqueTransferlessLinks.length === 1 ? uniqueTransferlessLinks[0] : uniqueTransferlessLinks.join("\n"),
				);

				// Configure event site selection
				const uniqueEventSites = Array.from(new Set(data.map((item) => item.internalNote.eventSite)));
				if (uniqueEventSites.length === 1 && uniqueEventSites[0]) {
					const vendorId = data[0].internalNote.vendorId;
					setSelectedVendorId(vendorId);
					const selectedEventSite = eventSites.find((site) => site.label === uniqueEventSites[0]) || null;
					setChosenEventSite(selectedEventSite);
					if (selectedEventSite && !selectedEventSite.value.vendorId && vendorId) {
						const selectedEventSiteDetail = eventSiteDetails.find((detail) => detail.vendorId === vendorId);
						if (selectedEventSiteDetail) {
							setChosenEventSiteDetail({
								value: selectedEventSiteDetail,
								label: selectedEventSiteDetail.name,
							});
							getEventSiteOptions(selectedEventSite.value.vendorParentId);
							setLinkUrl(selectedEventSiteDetail.url);
						}
					}
				} else {
					setChosenEventSite(null);
				}

				// Group accounts by email
				const groupedAccounts = data.reduce<Record<string, Account>>((acc, item) => {
					const email = item.internalNote.email;
					if (!acc[email]) {
						acc[email] = {
							id: uuidv4(),
							orderId: item.internalNote.orderId,
							email,
							// TAC-2365: never hydrate the password from the API response. The field is
							// write-only — left blank, the existing credential is preserved on save; a
							// typed value sets a new one.
							password: "",
							seats: [{ label: item.seat, value: item.itemId }],
							itemStatusIds: [item.itemStatusId],
						};
					} else {
						acc[email].seats.push({ label: item.seat, value: item.itemId });
						acc[email].itemStatusIds.push(item.itemStatusId);
					}
					return acc;
				}, {});
				const accountsArray = Object.values(groupedAccounts);
				setAccounts(accountsArray);

				// Set initial password visibility
				const initialPasswordVisible = accountsArray.reduce<Record<string, boolean>>((acc, account) => {
					acc[account.id] = !!account.password;
					return acc;
				}, {});
				setPasswordVisible(initialPasswordVisible);

				// Set masked details if they exist
				const maskedItem = data.find(
					(item) => item.internalNote.maskedDetails && Object.keys(item.internalNote.maskedDetails).length > 0,
				);
				if (maskedItem && maskedItem.internalNote.maskedDetails) {
					setIsMasked(true);
					const { section, row, seat } = maskedItem.internalNote.maskedDetails;
					setMaskedSection(section || defaultMaskedSection);
					setMaskedRow(row || defaultMaskedRow);
					setMaskedSeat(seat || defaultMaskedSeat);
				} else {
					setIsMasked(false);
					setMaskedSection(defaultMaskedSection);
					setMaskedRow(defaultMaskedRow);
					setMaskedSeat(defaultMaskedSeat);
				}
				setIsLoading(false);
			})
			.catch((error) => {
				console.error("Error fetching internal notes:", error);
				setIsLoading(false);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventSites, eventSiteDetails, invoiceId, listingId]);

	const theme = useAppSelector((state) => state.settings.theme);
	const compactSelectStyles = getCompactSelectStyles(theme);
	const seatSelectStyles = getCompactSelectStyles(theme, true);

	const accountContainer = (account: Account) => (
		<div className={styles.accountContainer}>
			<div className={styles.field} data-grow="seats">
				<div className={styles.label}>Seats</div>
				<Select
					options={seatOptions}
					value={account.seats}
					isMulti
					closeMenuOnSelect={false}
					onChange={(selected) => {
						const selectedMulti = (selected as MultiValue<SelectOption<string>>) ?? [];
						const updatedAccounts = accounts.map((acc) => {
							const newSeats =
								acc.id === account.id
									? [...selectedMulti]
									: acc.seats.filter((seat) => !selectedMulti.some((newSeat) => newSeat.value === seat.value));

							const newItemStatusIds = selectedMulti.map((seat) => seatToItemStatusMap[seat.label] || 0);

							return {
								...acc,
								seats: newSeats,
								itemStatusIds: newItemStatusIds,
							};
						});
						setAccounts(updatedAccounts.map((account) => ({ ...account, seats: [...account.seats] })));
					}}
					styles={seatSelectStyles}
				/>
			</div>
			<div className={styles.field}>
				<div className={styles.label}>Order ID</div>
				<UniversalInput
					type="text"
					value={account.orderId}
					placeholder={"Order ID"}
					onChange={(e) => updateAccount(account.id, { orderId: e.target.value })}
				/>
			</div>
			<div className={styles.field}>
				<div className={styles.label}>Email</div>
				<UniversalInput
					type="text"
					placeholder={"Email"}
					value={account.email}
					onChange={(e) => updateAccount(account.id, { email: e.target.value })}
				/>
			</div>
			<div className={styles.field}>
				<div className={styles.label}>Password</div>
				{passwordVisible[account.id] ? (
					<div className={styles.passwordRow}>
						{/* TAC-2365: write-only. The stored password is never returned by the API, so this
						    starts blank — leave it empty to keep the current password, or type to set a new one. */}
						<UniversalInput
							type="text"
							placeholder={"Enter new password"}
							value={account.password}
							onChange={(e) => updateAccount(account.id, { password: e.target.value })}
						/>
						<XIcon color={"red"} onClick={() => togglePasswordVisibility(account.id)} />
					</div>
				) : (
					<a onClick={() => togglePasswordVisibility(account.id)}>
						<u>Set / change password</u>
					</a>
				)}
			</div>
		</div>
	);

	return (
		<CommonModal
			controlSize="l"
			closeModal={onClose}
			title="Internal Notes"
			content={
				isLoading ? (
					<div className={styles.loaderWrapper}>
						<Loader controlSize={"l"} />
					</div>
				) : (
					<div className={styles.inModal}>
						{eventHeaderListings?.length ? (
							<EventContextHeader listings={eventHeaderListings} events={eventHeaderEvents} />
						) : null}
						<div className={styles.wrapper}>
							<div className={styles.vBox} data-has-right-divider="true">
								<div className={styles.inContent} data-is-divider="true">
									<div className={styles.subHeader}>Listing Info</div>
									<div className={styles.eventSiteWrapper}>
										<div className={styles.fieldGroup} data-grow="event-site">
											<div className={styles.label}>Event Site</div>
											<Select
												options={eventSites}
												value={chosenEventSite}
												onChange={handleEventSiteChange}
												components={makeAnimated()}
												closeMenuOnSelect={true}
												isMulti={false}
												isSearchable={true}
												styles={compactSelectStyles}
											/>
										</div>
										{chosenEventSite && !chosenEventSite.value.vendorId && (
											<>
												<div className={styles.fieldGroup} data-grow="event-site">
													<div className={styles.label}>Account Manager</div>
													<Select
														options={filteredEventSites}
														value={chosenEventSiteDetail}
														onChange={handleManagerChange}
														closeMenuOnSelect={false}
														isMulti={false}
														styles={compactSelectStyles}
													/>
												</div>
												<div className={styles.fieldGroup} data-grow="event-site">
													<UniversalInput
														type={"text"}
														placeholder={"Link"}
														value={linkUrl}
														onChange={(e) => setLinkUrl(e.target.value)}
														label={"Link"}
														isCopyable={true}
													/>
												</div>
											</>
										)}
									</div>

									<div className={styles.wrapperCentered}>
										<div className={styles.fieldGroup} data-grow="masked">
											<UniversalInput
												type={"text"}
												placeholder={"Section"}
												value={maskedSection}
												onChange={(e) => setMaskedSection(e.target.value)}
												label={"Section"}
												isDisabled={!isMasked}
												isCopyable={true}
											/>
										</div>
										<div className={styles.fieldGroup} data-grow="masked">
											<UniversalInput
												type={"Row"}
												placeholder={"Row"}
												value={maskedRow}
												onChange={(e) => setMaskedRow(e.target.value)}
												label={"Row"}
												isDisabled={!isMasked}
												isCopyable={true}
											/>
										</div>
										<div className={styles.fieldGroup} data-grow="masked">
											<UniversalInput
												type={"Seat"}
												placeholder={"Seat"}
												value={maskedSeat}
												onChange={(e) => setMaskedSeat(e.target.value)}
												label={"Seat"}
												isDisabled={!isMasked}
												isCopyable={true}
											/>
										</div>
										<div className={styles.maskedToggle}>
											<Switch
												checked={isMasked}
												onChange={() => setIsMasked((x) => !x)}
												tooltip="If you need to inform our team that the section, row, or seats are different from what is displayed, check the 'Is Masked' option. This helps our team accurately locate your tickets."
												label="Is Masked"
												tooltipId={"is-masked-internal-notes"}
											/>
										</div>
									</div>
									<div className={styles.flagToggles}>
										<div className={styles.flagToggle}>
											<Switch
												checked={noPasswordManager}
												onChange={(v) => handleFlagToggle("noPasswordManager", v)}
												label="No Password Manager"
											/>
										</div>
										<div className={styles.flagToggle}>
											<Switch
												checked={anonymousTransfer}
												onChange={(v) => handleFlagToggle("anonymousTransfer", v)}
												label="Anonymous Transfer"
											/>
										</div>
									</div>
								</div>
								<div className={styles.accountsList}>
									{accounts.map((account) => (
										<div key={account.id} className={styles.singleAccountContainer}>
											{accountContainer(account)}
											<Button
												onClick={() => removeAccount(account.id)}
												disabled={accounts.length === 1}
												variant={ButtonVariant.Secondary}>
												<Trashcan />
											</Button>
										</div>
									))}
								</div>
								<div className={styles.addAccountWrapper}>
									<Button onClick={addAccount} controlSize="sm" variant={ButtonVariant.Secondary}>
										<PlusIconThemed />
										<span style={{ marginLeft: 6, fontWeight: 500 }}>Add Account</span>
									</Button>
								</div>
							</div>
							<div className={styles.vBox} data-small="true">
								<div className={styles.inContent}>
									<div className={styles.subHeader}>Transfer</div>
									<div>
										<div className={styles.label}>Wallet Link</div>
										<textarea
											placeholder={"Input all Wallet Links that are required for transferless events."}
											value={transferlessLink}
											onChange={(e) => setTransferlessLink(e.target.value)}
											className={styles.minimalTextArea}
											style={{ resize: "none" }}
										/>
									</div>
									<div className={styles.divider} data-is-horizontal="true" />
									<div>
										<div className={styles.label}>Exclusion List</div>
										<Select
											options={abbreviatedEventSites.map((site) => ({
												value: site.abbr,
												label: site.name,
											}))}
											value={selectedExclusions}
											isMulti={true}
											closeMenuOnSelect={false}
											onChange={handleExclusionChange}
											styles={compactSelectStyles}
										/>
									</div>
									<div>
										<div className={styles.label}>Comments</div>
										<textarea
											className={styles.textArea}
											placeholder="Comments"
											value={comments}
											onChange={(e) => setComments(e.target.value)}
											style={{ resize: "none" }}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				)
			}
			buttons={
				<div className={styles.buttonsContainer}>
					<Button onClick={onClose} variant={ButtonVariant.Secondary}>
						Close
					</Button>
					<Button onClick={save} disabled={shouldDisableSave()} variant={ButtonVariant.Primary}>
						Save
					</Button>
				</div>
			}
		/>
	);
};

export default InternalNotesModal;
